import ABRCodeSelect from 'Components/Generic/BOSFilterSelect/ABRCodeSelect';
import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import SaveButton from 'Components/Generic/Buttons/SaveButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import { useAlertContext } from 'Data/context/Alerts';
import { user_roles } from 'Data/Enum';
import ExpenseCodeData from 'Data/ExpenseCodeData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { isUserAuthorized } from 'Util';

let DM = new DataManager();

const ExpenseCodeDetails = ({ expenseCodeId, expenseCodeObject, ...props }) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const {
    currentObject: expenseCode,
    hasBeenUpdated,
    reset: resetExpenseCode,
    updateField,
    originalObject: originalExpenseCode,
    doSave,
  } = useUpdatableObject(
    ExpenseCodeData.updateRecord,
    ExpenseCodeData.createRecord,
  );
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalExpenseCode,
    undoFunction: ExpenseCodeData.updateRecord,
    onUndo: (originalEC) => fetchExpenseCode(originalEC.id),
    objectType: 'Expense Code',
  });

  let editable = isUserAuthorized({
    roles: [user_roles.ADMINISTRATOR],
  });

  useEffect(() => {
    DM.add(
      ExpenseCodeData.registerListener(
        'BLANK_RECORD_LOADED',
        onExpenseCodeLoaded,
      ),
      ExpenseCodeData,
    );
    DM.add(
      ExpenseCodeData.registerListener('RECORD_LOADED', onExpenseCodeLoaded),
      ExpenseCodeData,
    );
    DM.add(
      ExpenseCodeData.registerListener('RECORD_UPDATED', onExpenseCodeUpdated),
      ExpenseCodeData,
    );
    DM.add(
      ExpenseCodeData.registerListener('RECORDS_DELETED', onECDeleted),
      ExpenseCodeData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    if (!expenseCodeId) {
      ExpenseCodeData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (expenseCodeId > 0) {
      fetchExpenseCode(expenseCodeId);
    }
  }, [expenseCodeId]);

  useEffect(() => {
    if (!editable) {
      resetExpenseCode(expenseCodeObject);
    } else if (expenseCodeObject?.id && Number(expenseCodeObject.id) >= 0) {
      fetchExpenseCode(expenseCodeObject.id);
    }
  }, [expenseCodeObject]);

  const fetchExpenseCode = (id) => {
    ExpenseCodeData.getRecord(id);
  };

  const onExpenseCodeLoaded = (payload) => {
    if (payload) {
      resetExpenseCode(payload);
      resetUndo(() => fetchExpenseCode(payload.id));
    }
  };

  const onExpenseCodeUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  let delCancelButton =
    editable &&
    (expenseCodeId && editable ? (
      <DeleteButton
        onDeleteItem={() => {
          deleteClicked();
        }}
        tooltipText={'Delete Expense Code'}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const deleteEC = () => {
    ExpenseCodeData.deleteRecords([expenseCodeId]);
  };

  const deleteClicked = () => {
    openDeleteDialog({
      objectType: 'Expense Code',
      onConfirmDelete: deleteEC,
    });
  };

  const onECDeleted = () => {
    showDeletedToast('Expense Code');
  };

  let detailData = [
    {
      label: 'Expense Code',
      value: expenseCode.expense_code,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('expense_code', newValue);
      },
      isRequired: true,
    },
    {
      label: 'ABR Code',
      value: {
        render: (inEditState) => {
          return inEditState ? (
            <ABRCodeSelect
              defaultSelectedABR={expenseCode.abr_code_fk}
              onSelectABRCode={(abr) => {
                abr && updateField('abr_code_fk', abr.abr_code);
              }}
            />
          ) : (
            expenseCode.abr_code_fk
          );
        },
      },
      isRequired: true,
    },
    {
      label: 'Description',
      value: expenseCode.description,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('description', newValue);
      },
    },
    {
      label: 'Range Start',
      value: expenseCode.serial_range_start,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('serial_range_start', newValue);
      },
    },
    {
      label: 'Range End',
      value: expenseCode.serial_range_end,
      editableFields: ['value'],
      onEdit: (newValue) => {
        updateField('serial_range_end', newValue);
      },
    },
  ];

  const undoButton = editable && expenseCode.id && hasBeenUpdated && (
    <UndoButton clickHandler={() => fetchExpenseCode(expenseCode.id)} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <SaveButton clickHandler={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    expenseCode && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <EditableDetailPanel
          editable={editable}
          renderChild={(inEditState) => {
            return <DetailTable data={detailData} editable={inEditState} />;
          }}
          footer={footer}
          {...props}
        />
        {delCancelButton}
      </div>
    )
  );
};

ExpenseCodeDetails.propTypes = {
  expenseCodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expenseCodeObject: PropTypes.object,
};

export default ExpenseCodeDetails;
